import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { 
  Card, 
  List, 
  Typography, 
  Space, 
  Tag, 
  Button, 
  Skeleton, 
  Empty,
  Divider,
  Badge,
  Avatar,
  Image,
  Breadcrumb,
  Alert, 
  Modal,
  Row,
  Col
} from 'antd';
import { 
  HomeOutlined, 
  PhoneOutlined, 
  EnvironmentOutlined, 
  StarOutlined,
  WifiOutlined,
  CarOutlined,
  CoffeeOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SyncOutlined,
  ArrowLeftOutlined,
  DesktopOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  FireOutlined,
  TrophyOutlined,
  HeatMapOutlined,
  ShopOutlined,
  LockOutlined,
  VideoCameraOutlined,
  UserOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  AuditOutlined
} from '@ant-design/icons';
import './AmenitiesPage.css';

const { Title, Text } = Typography;

const AmenitiesPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const breadcrumbItems = [
    {
      title: <Link to={authData?.role?.toLowerCase() === 'systemadmin' ? '/admin/dashboard' : '/dashboard'}>Home</Link>,
    },
    {
      title: 'Hotels & Amenities',
    },
  ];

  const facilityIcons = {
    // Connectivity
    'WiFi': <WifiOutlined />,
    'Internet': <WifiOutlined />,
    'Business Center': <DesktopOutlined />,
    
    // Transportation
    'Parking': <CarOutlined />,
    'Valet Parking': <CarOutlined />,
    'Airport Shuttle': <CarOutlined />,
    'Bicycle Rental': <CarOutlined />,
    
    // Food & Beverage
    'Restaurant': <CoffeeOutlined />,
    'Bar/Lounge': <CoffeeOutlined />,
    'Breakfast': <CoffeeOutlined />,
    'Room Service': <CoffeeOutlined />,
    
    // Recreation
    'Pool': <SafetyOutlined />,
    'Spa': <MedicineBoxOutlined />,
    'Fitness Center': <HeartOutlined />,
    'Gym': <HeartOutlined />,
    'Sauna': <FireOutlined />,
    'Tennis Court': <TrophyOutlined />,
    'Golf Course': <TrophyOutlined />,
    
    // Room Amenities
    'Air Conditioning': <HeatMapOutlined />,
    'Mini Bar': <ShopOutlined />,
    'Safe': <LockOutlined />,
    'TV': <VideoCameraOutlined />,
    
    // Services
    'Concierge': <UserOutlined />,
    'Laundry': <ClockCircleOutlined />,
    'Dry Cleaning': <ClockCircleOutlined />,
    '24-Hour Front Desk': <ClockCircleOutlined />,
    
    // Family
    'Kids Club': <TeamOutlined />,
    'Babysitting': <TeamOutlined />,
    
    // Accessibility
    'Wheelchair Access': <EyeOutlined />,
    'Elevator': <ArrowUpOutlined />,
    
    // Other
    'Meeting Rooms': <AuditOutlined />,
    'Banquet Hall': <AuditOutlined />,
    'Garden': <EnvironmentOutlined />,
    'Terrace': <EnvironmentOutlined />,
    'Beach Access': <EnvironmentOutlined />,
    
    // Default fallback
    'Other': <StarOutlined />
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/Hotel');
      setHotels(response.data || []);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      setError('Failed to load hotels. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchHotels();
  };

  return (
    <div className="amenities-page">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <Button 
              type="text" 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(-1)}
            />
            <Text strong>Hotels & Amenities</Text>
            <Badge 
              count={hotels.length} 
              showZero 
              style={{ backgroundColor: '#1890ff' }} 
            />
            <Button 
              type="text" 
              icon={<InfoCircleOutlined />} 
              onClick={() => setShowInfoModal(true)}
            />
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => navigate('/hotels/add-hotel')}
            >
              Add Hotel
            </Button>
            <Button 
              icon={<SyncOutlined />} 
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button 
                size="small" 
                type="primary"
                onClick={handleRefresh}
              >
                Retry
              </Button>
            }
          />
        )}

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : hotels.length === 0 ? (
          <Empty
            description={
              <Space direction="vertical">
                <Text>No hotels found</Text>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/hotels/add-hotel')}
                >
                  Add Hotel
                </Button>
              </Space>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            itemLayout="vertical"
            size="large"
            dataSource={hotels}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: total => `Total ${total} hotels`
            }}
            renderItem={(hotel) => (
              <List.Item
                key={hotel.hotelId}
                extra={
                  hotel.imageUrl && (
                    <Image
                      width={200}
                      alt={hotel.hotelName}
                      src={hotel.imageUrl}
                      style={{ borderRadius: 8 }}
                      preview={false}
                    />
                  )
                }
              >
                <List.Item.Meta
                  avatar={<Avatar size={64} icon={<HomeOutlined />} />}
                  title={<Title level={4}>{hotel.hotelName}</Title>}
                  description={
                    <Space direction="vertical" size="small">
                      <Text>
                        <EnvironmentOutlined /> {hotel.address}
                      </Text>
                      <Text>
                        <PhoneOutlined /> {hotel.contactNumber}
                      </Text>
                      {hotel.rating && (
                        <Text>
                          <StarOutlined /> Rating: {hotel.rating}/5
                        </Text>
                      )}
                    </Space>
                  }
                />
                
                {hotel.facilities && hotel.facilities.length > 0 && (
                  <>
                    <Divider orientation="left">Amenities</Divider>
                    <Space wrap>
                      {hotel.facilities.map((facility, index) => (
                        <Tag 
                          key={index} 
                          icon={facilityIcons[facility] || <StarOutlined />}
                          color="blue"
                          style={{ marginBottom: 4 }}
                        >
                          {facility}
                        </Tag>
                      ))}
                    </Space>
                  </>
                )}
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Info Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Hotels & Amenities Guide</Text>
          </Space>
        }
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button 
            key="close" 
            type="primary" 
            onClick={() => setShowInfoModal(false)}
          >
            Close
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '8px 0' }}>
          <Text>
            This module displays all hotels and their available amenities in the system.
          </Text>
          
          <Divider orientation="left" style={{ margin: '16px 0' }}>Key Features</Divider>
          <ul style={{ paddingLeft: 20 }}>
            <li>View complete hotel information including contact details</li>
            <li>See all available amenities for each property</li>
            <li>Filter hotels by amenities or rating</li>
            <li>Add new hotels to the system</li>
          </ul>

          <Divider orientation="left" style={{ margin: '16px 0' }}>Current Status</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Text strong>Total Hotels:</Text> {hotels.length}
            </Col>
            <Col span={12}>
              <Text strong>Last Updated:</Text> {new Date().toLocaleString()}
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default AmenitiesPage;